import { inject } from '@angular/core';
import {
  CanActivateFn,
  CanMatchFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Route,
  UrlSegment
} from '@angular/router';
import { map, take, timeout, catchError, filter, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ContextService } from '../services/context.service';
import { Router } from '@angular/router';

/**
 * Fast authentication check for canMatch
 * Only verifies user is authenticated - does NOT wait for context initialization
 * This prevents lazy routes from loading when user is not authenticated
 */
export const authCanMatch: CanMatchFn =
  (route: Route, segments: UrlSegment[]) => {
    const authService = inject(AuthService);
    const url = '/' + segments.map(s => s.path).join('/');

    if (url.startsWith('/callback')) {
      return of(true);
    }

    return authService.isLoading$.pipe(
      filter(loading => loading === false), // esperar a que Auth0 resuelva sesión
      take(1),
      switchMap(() => authService.isAuthenticated$.pipe(take(1))),
      map(isAuthenticated => {
        if (!isAuthenticated) {
          authService.login();
          return false;
        }
        return true;
      })
    );
  };

/**
 * Context readiness check for canActivate
 * Waits for context to be fully initialized before allowing navigation
 * This ensures permissions, portal context, etc. are ready before rendering
 */
export const contextReadyCanActivate: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const contextService = inject(ContextService);
    const router = inject(Router);
    const url = state.url;

    // Skip guard logic on /callback route - initialization happens there
    if (url.startsWith('/callback')) {
      console.log('AuthGuard: Skipping contextReadyCanActivate on /callback route');
      return of(true);
    }

    console.log('AuthGuard: Waiting for context ready for route:', url);

    if (contextService.isSessionRejected) {
      router.navigate(['/unauthorized'], { replaceUrl: true });
      return of(false);
    }

    return contextService.contextReady$.pipe(
      filter(contextReady => contextReady === true),
      take(1),
      timeout(20000),
      map(() => {
        console.log('AuthGuard: Context ready, ALLOWING access to:', url);
        return true;
      }),
      catchError(error => {
        console.warn('AuthGuard: Timeout or error waiting for context for route:', url, error);
        authService.login();
        return of(false);
      })
    );
  };
